import {
  DollarSign,
  Search,
  Plus,
  Menu,
  X,
  LogIn,
  LogOut,
  Smartphone,
  User,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/Urls";
import ThemeToggle from "./ThemeToggle";
import NotificationBell from "./NotificationBell";
import { toast } from "../hooks/use-toast";
import {
  clearAuthSession,
  getStoredToken,
  getStoredUserLocation,
} from "../lib/authStorage";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      setUser(token);
    }
  }, []);

  const handlePostItem = () => {
    const token = getStoredToken();

    if (!token) {
      alert("Please sign in before posting an item.");
      navigate("/signin");
      return;
    }

    const { country, city, location } = getStoredUserLocation();

    if (!country || !city || !location) {
      alert(
        "Please go to your profile and set your country, city and location before posting an item."
      );
      navigate("/profile");
      return;
    }

    navigate("/post");
  };


  const handleSignOut = async () => {
    const confirmed = window.confirm("Do you want to logout?");

    if (!confirmed) return;

    setLoading(true);

    const token = getStoredToken();

    try {
      await api.post(
        "/api/users/logout",
        { token },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Logout successful");
    } catch (error) {
      console.warn("Logout API failed, forcing logout anyway", error);
    } finally {
      setUser(null);
      clearAuthSession();
      setLoading(false);
      navigate(0);
    }
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="container mx-auto flex items-center justify-between px-4 py-4">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
            <DollarSign className="h-5 w-5 text-primary-foreground" />
          </div>

          <span className="font-heading text-xl font-bold text-foreground">
            Deal<span className="text-primary">Swapify</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden items-center gap-6 md:flex">
          <Link
            to="/#categories"
            className="text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            Browse
          </Link>

          <Link
            to="/how-it-works"
            className="text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            How It Works
          </Link>

          <Link
            to="/about"
            className="text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            About
          </Link>
        </div>

        {/* Desktop actions */}
        <div className="hidden items-center gap-3 md:flex">
          <ThemeToggle />

          <Link
            to="/search"
            className="flex items-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2 text-sm text-secondary-foreground transition-colors hover:bg-muted"
          >
            <Search className="h-4 w-4" />
            Search
          </Link>

          {/* GET THE APP */}
          <button
            type="button"
            onClick={() =>
              toast({
                title: "App is coming soon!",
                description: "The DealSwapify mobile app will be available soon.",
                duration: 3000,
              })
            }
            className="
              flex
              items-center
              gap-2
              rounded-lg
              border
              border-primary/40
              bg-primary/10
              px-4
              py-2
              text-sm
              font-medium
              text-primary
              transition-all
              hover:border-primary
              hover:bg-primary/15
            "
          >
            <Smartphone className="h-4 w-4" />
            Get the App
          </button>

          <NotificationBell />

          {user && (
            <Link
              to="/profile"
              className="flex items-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2 text-sm text-secondary-foreground transition-colors hover:bg-muted"
            >
              <User className="h-4 w-4" />
              Profile
            </Link>
          )}

          <button
            onClick={handlePostItem}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-all hover:shadow-[var(--glow-primary)]"
          >
            <Plus className="h-4 w-4" />
            Post Item
          </button>

          {user ? (
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2 text-sm text-secondary-foreground transition-colors hover:bg-muted"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          ) : (
            <Link
              to="/signin"
              className="flex items-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2 text-sm text-secondary-foreground transition-colors hover:bg-muted"
            >
              <LogIn className="h-4 w-4" />
              Sign In
            </Link>
          )}
        </div>

        {/* Mobile actions */}
        <div className="flex items-center gap-3 md:hidden">
          <ThemeToggle />

          <NotificationBell compact />

          <button
            className="text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-border bg-background px-4 pb-4 md:hidden">
          <div className="flex flex-col gap-3 pt-3">
            <Link
              to="/#categories"
              className="text-sm text-muted-foreground"
              onClick={() => setMobileOpen(false)}
            >
              Browse
            </Link>

            <Link
              to="/search"
              className="flex items-center gap-2 text-sm text-muted-foreground"
              onClick={() => setMobileOpen(false)}
            >
              Search
            </Link>

            <Link
              to="/how-it-works"
              className="text-sm text-muted-foreground"
              onClick={() => setMobileOpen(false)}
            >
              How It Works
            </Link>

            <Link
              to="/about"
              className="text-sm text-muted-foreground"
              onClick={() => setMobileOpen(false)}
            >
              About
            </Link>

            {/* GET THE APP */}
            <button
              type="button"
              onClick={() => {
                
                toast({
                  title: "App is coming soon!",
                  description: "The DealSwapify mobile app will be available soon.",
                  duration: 3000,
                });
              }}
              className="
                flex
                items-center
                justify-center
                gap-2
                rounded-lg
                border
                border-primary/40
                bg-primary/10
                px-4
                py-2.5
                text-sm
                font-medium
                text-primary
                transition-colors
                hover:bg-primary/15
              "
            >
              <Smartphone className="h-4 w-4" />
              Get the App
            </button>

            <button
              onClick={() => {
                handlePostItem();
                setMobileOpen(false);
              }}
              className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-all hover:shadow-[var(--glow-primary)]"
            >
              <Plus className="h-4 w-4" />
              Post Item
            </button>

            {user && (
              <Link
                to="/profile"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2 text-sm text-secondary-foreground"
              >
                <User className="h-4 w-4" />
                Profile
              </Link>
            )}

            {user ? (
              <button
                onClick={() => {
                  handleSignOut();
                  setMobileOpen(false);
                }}
                className="flex items-center justify-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2 text-sm text-secondary-foreground"
              >
                <LogOut className="h-4 w-4" />
                {loading ? "Signing out..." : "Sign Out"}
              </button>

            ) : (
              <Link
                to="/signin"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center gap-2 rounded-lg border border-border bg-secondary px-4 py-2 text-sm text-secondary-foreground"
              >
                <LogIn className="h-4 w-4" />
                Sign In
              </Link>
              
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
